import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Ban, Eye, Pencil, RotateCcw } from 'lucide-react';
import type { CursorPage, PhysicianRow } from '@dru/types';
import {
  Button, ConfirmDialog, DataTable, FilterBar, IconAction, RowActions, StatusBadge, TextField,
  Alert, InfoTip, Input, SearchInput, useToast,
} from '@dru/ui';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { fetchAll } from '../../../lib/fetchAll';
import { errorMessage, useAuth } from '../../../lib/auth';
import { PageHeader, holds } from '../../../layouts/ConsoleShell';
import { Dialog } from '../../../components/Dialog';


export type PanelRow = PhysicianRow & { status: string; linked: boolean };

/**
 * Whether somebody on the panel can sign today, and if not, every reason.
 *
 * Three facts decide it, and a row that only showed them as three separate
 * badges left the reader to do the AND in their head across nine columns.
 */
export function signingOf(p: {
  status?: string | null; afihCertified?: boolean | null; linked?: boolean | null;
  loginStatus?: string | null; loginCompanyBound?: boolean | null;
}) {
  const reasons: string[] = [];
  if (p.status && p.status !== 'ACTIVE') reasons.push('Suspended');
  if (!p.afihCertified) reasons.push('Not AFIH certified');
  if (p.linked === false) reasons.push('No console login');
  else if (p.loginStatus === 'INVITED') reasons.push('Invitation not accepted yet');
  else if (p.loginStatus && p.loginStatus !== 'ACTIVE') reasons.push('Login suspended');
  // The older shape: reads one company only, so other companies' cases are
  // never given to it. Not a block on signing that company's.
  return { canSign: reasons.length === 0, reasons, companyBound: !!p.loginCompanyBound };
}

/**
 * The certifying panel.
 *
 * Three facts decide whether somebody can sign a fitness certificate: the
 * registration number that appears on the document, whether the Factories Act
 * recognises the signature (AFIH), and whether the person has a login at all.
 * A physician with no linked account can be assigned an examination and can
 * never certify it, which is the kind of gap that surfaces at the worst
 * moment — so the list answers "can sign?" in one column, with the reason.
 *
 * A row opens the physician; the rate card and statements live there.
 */
export function Physicians() {
  const nav = useNavigate();
  const { permissions } = useAuth();
  // Adding to the panel is `user.create`, correcting or suspending an entry
  // `user.update` — the API's own checks, mirrored so nobody is offered a 403.
  const canAdd = holds(permissions, 'user.create');
  const canEdit = holds(permissions, 'user.update');
  const [adding, setAdding] = useState(false);
  const [params, setParams] = useSearchParams();
  const sign = params.get('sign') ?? '';
  const status = params.get('status') ?? '';
  const setFilters = (next: Record<string, string | undefined>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) { if (v) p.set(k, v); else p.delete(k); }
    setParams(p, { replace: true });
  };

  // Searched on the server and followed to the end of the cursor. A single
  // call capped at 200 with no search box rendered every physician at once,
  // and silently dropped the 201st.
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const panel = useQuery<PanelRow[]>(
    () => fetchAll<PanelRow>((cursor) => api.get<CursorPage<PanelRow>>(
      `/physicians?afihOnly=false&includeInactive=true&limit=200`
      + (q ? `&q=${encodeURIComponent(q)}` : '')
      + (cursor ? `&cursor=${cursor}` : ''),
    )),
    [q],
  );

  const all = panel.data ?? [];
  // The "needs attention" notice was removed (owner, 29 Oct 2026); the
  // Signing filter still finds the physicians who cannot sign.
  const rows = all.filter((p) => (!status || p.status === status)
    && (!sign || (sign === 'can') === signingOf(p).canSign));
  const filtered = Boolean(sign || status || q);

  return (
    <div className="space-y-4">
      <PageHeader
        className="mb-0"
        title="Physicians"
        hint="The certifying panel: who may sign a statutory fitness certificate, and who cannot yet."
        actions={canAdd && <Button onClick={() => setAdding(true)}>Add a physician</Button>}
      />

      <FilterBar
        filters={[
          {
            key: 'sign', label: 'Signing',
            options: [
              { value: 'can', label: 'Can sign' },
              { value: 'cannot', label: 'Cannot sign' },
            ],
          },
          {
            key: 'status', label: 'Status',
            options: [
              { value: 'ACTIVE', label: 'Active' },
              { value: 'SUSPENDED', label: 'Suspended' },
            ],
          },
        ]}
        value={{ sign: sign || undefined, status: status || undefined }}
        onChange={setFilters}
      >
        <SearchInput
          value={search}
          onValueChange={setSearch}
          placeholder="Name, registration or city"
          aria-label="Search physicians"
          className="w-full sm:w-72"
        />
      </FilterBar>

      <DataTable<PanelRow>
        rows={rows}
        loading={panel.loading}
        error={panel.error}
        onRetry={panel.reload}
        rowKey={(p) => p.id}
        onRowClick={(p) => nav(`/admin/physicians/${p.id}`)}
        empty={filtered
          ? { title: 'Nobody on the panel matches', hint: 'Try the name, the registration number or the town — or clear the filters.' }
          : {
              title: 'Nobody on the panel',
              hint: 'Without an AFIH-certified physician with a console login, no examination can ever be certified.',
              action: canAdd ? <Button onClick={() => setAdding(true)}>Add a physician</Button> : undefined,
            }}
        columns={[
          {
            key: 'name', header: 'Physician', alwaysVisible: true,
            cell: (p) => (
              <div className="min-w-0">
                <div className="font-medium text-neutral-900">{p.fullName}</div>
                <div className="text-caption text-neutral-500">
                  {p.qualification ? `${p.qualification} · ` : ''}
                  <span className="font-mono">{p.registrationNo}</span>
                </div>
                {/* Contact sits under the name, so the table keeps five
                    columns and fits a tablet as a table, not as cards. */}
                <div className="text-caption text-neutral-500">
                  {p.mobile
                    ? <a href={`tel:${p.mobile}`} onClick={(e) => e.stopPropagation()} className="font-mono text-primary-700 hover:underline">{p.mobile}</a>
                    : 'No mobile'}
                  {p.city ? ` · ${p.city}` : ''}
                </div>
              </div>
            ),
          },
          {
            key: 'sign', header: 'Can sign?',
            cell: (p) => {
              const { canSign, reasons } = signingOf(p);
              return (
                <div>
                  <StatusBadge tone={canSign ? 'covered' : p.status !== 'ACTIVE' ? 'neutral' : 'danger'}>
                    {canSign ? 'Can sign' : 'Cannot sign'}
                  </StatusBadge>
                  <div className="mt-0.5 text-caption text-neutral-600">
                    {canSign
                      ? <span className="font-mono">{p.afihNumber ?? 'AFIH on record'}</span>
                      : reasons.join(' · ')}
                    {canSign && p.loginCompanyBound && <span className="block text-warning-600">One company only</span>}
                  </div>
                </div>
              );
            },
          },
          {
            key: 'reviews', header: 'Reviews', align: 'right',
            cell: (p) => (
              <div className="text-caption tabular-nums">
                <div className={(p.pendingReviews ?? 0) > 0 ? 'font-medium text-neutral-900' : 'text-neutral-600'}>
                  {p.pendingReviews ?? 0} pending
                </div>
                <div className="text-neutral-500">{p.completedReviews ?? 0} certified</div>
              </div>
            ),
          },
          {
            key: 'availability', header: 'Availability',
            cell: (p) => (
              <StatusBadge tone={p.isAvailable !== false ? 'covered' : 'neutral'}>
                {p.isAvailable !== false ? 'Available' : 'Away'}
              </StatusBadge>
            ),
          },
          {
            key: 'act', header: 'Actions', align: 'right', alwaysVisible: true, mobileLabel: '',
            cell: (p) => (
              <PhysicianRowActions
                physician={p}
                canEdit={canEdit}
                onView={() => nav(`/admin/physicians/${p.id}`)}
                onChanged={panel.reload}
              />
            ),
          },
        ]}
      />

      {adding && (
        <AddDialog
          onClose={() => setAdding(false)}
          onDone={() => { setAdding(false); panel.reload(); }}
        />
      )}
    </div>
  );
}

/**
 * Suspend and reinstate, both behind a confirmation.
 *
 * Suspending a certifying surgeon is not a list-row toggle: from that moment
 * the panel entry cannot sign, and any examination already routed to them
 * stalls on an order nobody can certify. So the dialog names the consequence
 * rather than asking "are you sure", and asks why — the reason is what the
 * audit row carries, and it is the answer to the question the stalled order
 * will provoke a week later.
 *
 * Reinstating is confirmed too, but without a reason: restoring the normal
 * state is not the act that needs explaining.
 */
function PhysicianRowActions({ physician, canEdit, onView, onChanged }: {
  physician: PanelRow;
  canEdit: boolean;
  onView: () => void;
  onChanged: () => void;
}) {
  const [confirming, setConfirming] = useState<null | 'SUSPENDED' | 'ACTIVE'>(null);
  const [editing, setEditing] = useState(false);
  const toast = useToast();

  return (
    <RowActions>
      <IconAction label={`View ${physician.fullName}`} icon={Eye} onClick={onView} />
      {canEdit && (
        <IconAction label={`Edit ${physician.fullName}`} icon={Pencil} onClick={() => setEditing(true)} />
      )}
      {canEdit && (physician.status === 'ACTIVE' ? (
        <IconAction
          label={`Suspend ${physician.fullName} — they stop being able to sign`}
          icon={Ban} danger
          onClick={() => setConfirming('SUSPENDED')}
        />
      ) : (
        <IconAction
          label={`Reinstate ${physician.fullName}`}
          icon={RotateCcw}
          onClick={() => setConfirming('ACTIVE')}
        />
      ))}

      {editing && (
        <PhysicianEditDialog
          physician={physician}
          onClose={() => setEditing(false)}
          onDone={() => {
            setEditing(false);
            toast.toast('success', `${physician.fullName} updated.`);
            onChanged();
          }}
        />
      )}

      {confirming && (
        <PhysicianStatusDialog
          physician={physician}
          next={confirming}
          onClose={() => setConfirming(null)}
          onDone={() => { setConfirming(null); onChanged(); }}
        />
      )}
    </RowActions>
  );
}

/**
 * Suspend and reinstate, both behind a confirmation. Shared with the
 * physician's own page, so the consequence is worded once.
 *
 * Suspending a certifying surgeon is not a list-row toggle: from that moment
 * the panel entry cannot sign, and any examination already routed to them
 * stalls on an order nobody can certify. So the dialog names the consequence
 * rather than asking "are you sure", and asks why — the reason is what the
 * audit row carries, and it is the answer to the question the stalled order
 * will provoke a week later.
 *
 * Reinstating is confirmed too, but without a reason: restoring the normal
 * state is not the act that needs explaining.
 */
export function PhysicianStatusDialog({ physician, next, onClose, onDone }: {
  physician: Pick<PanelRow, 'id' | 'fullName' | 'registrationNo'>;
  next: 'SUSPENDED' | 'ACTIVE';
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();

  // Errors propagate: ConfirmDialog stays open and shows them, because
  // closing over a failure tells the operator it worked.
  async function patch(status: 'SUSPENDED' | 'ACTIVE', reason: string) {
    await api.patch(`/physicians/${physician.id}`, {
      status,
      ...(reason ? { reason } : {}),
    });
    toast.toast(
      'success',
      status === 'SUSPENDED'
        ? `${physician.fullName} suspended. They can no longer sign.`
        : `${physician.fullName} reinstated.`,
    );
    onDone();
  }

  return next === 'SUSPENDED' ? (
    <ConfirmDialog
      title="Suspend this physician?"
      what={<>
        <strong>{physician.fullName}</strong> will no longer be able to sign
        fitness certificates, from the moment you confirm.
      </>}
      affects={<>
        Registration <span className="font-mono">{physician.registrationNo}</span>. Examinations already routed to
        this physician stay routed and cannot be certified until they are
        reinstated or reassigned. Certificates already signed remain valid.
      </>}
      confirmLabel="Suspend physician"
      tone="danger"
      requireReason
      reasonLabel="Why is this physician being suspended?"
      reasonHint="Recorded in the audit trail against your name."
      onConfirm={(reason) => patch('SUSPENDED', reason)}
      onCancel={onClose}
    />
  ) : (
    <ConfirmDialog
      title="Reinstate this physician?"
      what={<>
        <strong>{physician.fullName}</strong> will be able to sign fitness
        certificates again, provided they are AFIH certified and have a console login.
      </>}
      affects={<>Registration <span className="font-mono">{physician.registrationNo}</span>.</>}
      confirmLabel="Reinstate physician"
      onConfirm={() => patch('ACTIVE', '')}
      onCancel={onClose}
    />
  );
}

function AddDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [form, setForm] = useState({
    fullName: '', registrationNo: '', qualification: '',
    afihCertified: true, afihNumber: '', invite: true,
    mobile: '', email: '', city: '', state: '',
  });
  const toast = useToast();
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true); setError(null);
    try {
      await api.post('/physicians', {
        fullName: form.fullName,
        registrationNo: form.registrationNo,
        ...(form.qualification ? { qualification: form.qualification } : {}),
        afihCertified: form.afihCertified,
        ...(form.afihNumber ? { afihNumber: form.afihNumber } : {}),
        ...(form.mobile ? { mobile: form.mobile } : {}),
        ...(form.email ? { email: form.email.trim() } : {}),
        ...(form.city ? { city: form.city } : {}),
        ...(form.state ? { state: form.state } : {}),
        ...(form.invite && form.email.trim() ? { invite: true } : {}),
      }, crypto.randomUUID());
      toast.toast('success', form.invite && form.email.trim()
        ? `${form.fullName} added. An invitation to sign in was sent to ${form.email.trim()}.`
        : `${form.fullName} added to the panel.`);
      onDone();
    } catch (e) {
      // On the panel already, only the email did not go: say so and close,
      // so "Add" is not pressed again for an entry that exists.
      if ((e as { type?: string }).type === 'physician-added-not-invited') {
        toast.toast('warning', errorMessage(e));
        onDone();
        return;
      }
      setError(errorMessage(e));
    } finally { setBusy(false); }
  }

  return (
    <Dialog title="Add a physician" onClose={onClose}>
      <TextField label="Full name" value={form.fullName} autoFocus required
        onChange={(v) => setForm({ ...form, fullName: v })} />
      <TextField label="Medical council registration" value={form.registrationNo} mono required
        onChange={(v) => setForm({ ...form, registrationNo: v })}
        hint="Printed on every certificate this person signs. Cannot be changed later." />
      <TextField label="Qualification" value={form.qualification}
        onChange={(v) => setForm({ ...form, qualification: v })} />

      <div className="mt-4 flex items-center gap-1">
        <label className="flex items-center gap-2">
          <Input
            type="checkbox"
            checked={form.afihCertified}
            onChange={(e) => setForm({ ...form, afihCertified: e.target.checked })}
          />
          <span className="text-sm text-neutral-900">AFIH certified</span>
        </label>
        <InfoTip label="About AFIH certification">
          Associate Fellow of Industrial Health. Under the Factories Act the
          certifying surgeon must hold it, and certification is refused
          without it — whatever permissions the account has.
        </InfoTip>
      </div>

      {form.afihCertified && (
        <TextField label="AFIH number" value={form.afihNumber} mono required
          onChange={(v) => setForm({ ...form, afihNumber: v })}
          hint="The evidence behind the qualification." />
      )}

      {/* How to reach them. A panel that is only names and registration
          numbers cannot answer "who do I ring about this decision".
          Deliberately no venue: a physician belongs to Dr Ultimate and reviews
          work from any hospital, so recording one as a hospital's would be
          asserting something untrue. */}
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <TextField label="Mobile" value={form.mobile} mono
          onChange={(v) => setForm({ ...form, mobile: v.replace(/\D/g, '').slice(0, 10) })} />
        <TextField label="Email" value={form.email}
          onChange={(v) => setForm({ ...form, email: v })} />
        <TextField label="City" value={form.city}
          onChange={(v) => setForm({ ...form, city: v })} />
        <TextField label="State" value={form.state}
          onChange={(v) => setForm({ ...form, state: v })} />
      </div>

      {/* The login, by email — never by pasting an account id (flow audit
          2026-10-20, 12.2). The invitation creates a panel login, which reads
          every company's cases assigned to this physician. */}
      <div className="mt-4 flex items-center gap-1">
        <label className="flex items-center gap-2">
          <Input
            type="checkbox"
            checked={form.invite}
            onChange={(e) => setForm({ ...form, invite: e.target.checked })}
          />
          <span className="text-sm text-neutral-900">Invite them to sign in</span>
        </label>
        <InfoTip label="About the console login">
          Sends an email to the address above with a link to set a password. They need a login to sign
          certificates: certification checks that the person signing is the person named. One login
          reviews cases from every company.
        </InfoTip>
      </div>
      {form.invite && !emailOk && (
        <p className="mt-1 text-caption text-neutral-600">Add their email address above to send the invitation.</p>
      )}

      {error && <Alert>{error}</Alert>}

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          onClick={submit}
          loading={busy}
          disabled={
            form.fullName.trim().length < 2
            || form.registrationNo.trim().length < 4
            || (form.afihCertified && form.afihNumber.trim().length < 2)
            || (form.invite && !emailOk)
          }
        >
          {form.invite ? 'Add and invite' : 'Add to panel'}
        </Button>
      </div>
    </Dialog>
  );
}

/**
 * Correcting a panel entry.
 *
 * The registration number is deliberately absent: it is printed on every
 * certificate this person has already signed, so changing it would silently
 * re-attribute finished work. A wrong registration is a new entry and the old
 * one suspended, which leaves both facts on the record.
 */
export function PhysicianEditDialog({ physician, onClose, onDone }: {
  physician: Pick<PanelRow, 'id' | 'fullName' | 'registrationNo'> & Partial<PanelRow>;
  onClose: () => void;
  onDone: () => void;
}) {
  const [form, setForm] = useState({
    fullName: physician.fullName ?? '',
    qualification: (physician as { qualification?: string }).qualification ?? '',
    afihCertified: Boolean((physician as { afihCertified?: boolean }).afihCertified),
    afihNumber: (physician as { afihNumber?: string }).afihNumber ?? '',
    email: (physician as { email?: string }).email ?? '',
    mobile: (physician as { mobile?: string }).mobile ?? '',
    city: (physician as { city?: string }).city ?? '',
    state: (physician as { state?: string }).state ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof form) => (v: string) => setForm((f) => ({ ...f, [k]: v }));

  async function submit() {
    setBusy(true); setError(null);
    try {
      // An emptied box clears the field rather than leaving the old value:
      // these are all nullable, and "I deleted the phone number" should mean
      // the phone number is gone.
      await api.patch(`/physicians/${physician.id}`, {
        fullName: form.fullName.trim(),
        qualification: form.qualification.trim() || undefined,
        afihCertified: form.afihCertified,
        afihNumber: form.afihNumber.trim() || undefined,
        email: form.email.trim() || null,
        mobile: form.mobile.trim() || null,
        city: form.city.trim() || null,
        state: form.state.trim() || null,
      });
      onDone();
    } catch (e) { setError(errorMessage(e)); setBusy(false); }
  }

  return (
    <Dialog title={`Edit ${physician.fullName}`} onClose={onClose}>
      <TextField label="Full name" value={form.fullName} onChange={set('fullName')} autoFocus required />

      <div className="mt-3 rounded-input bg-neutral-50 px-3 py-2 text-caption text-neutral-600">
        Registration <span className="font-mono">{physician.registrationNo}</span> cannot be
        changed here.{' '}
        <InfoTip label="About the registration number">
          It is printed on every certificate this person has signed, so correcting
          it would re-attribute finished work — add a new panel entry and suspend this one instead.
        </InfoTip>
      </div>

      <TextField label="Qualification" value={form.qualification} onChange={set('qualification')} />

      <div className="mt-4 flex items-center gap-1">
        <label className="flex items-center gap-2">
          <Input
            type="checkbox"
            checked={form.afihCertified}
            onChange={(e) => setForm({ ...form, afihCertified: e.target.checked })}
          />
          <span className="text-sm text-neutral-900">AFIH certified</span>
        </label>
        <InfoTip label="About AFIH certification">
          Only an AFIH-certified physician may sign a statutory fitness certificate.
        </InfoTip>
      </div>

      {form.afihCertified && (
        <TextField label="AFIH number" value={form.afihNumber} onChange={set('afihNumber')} mono required />
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <TextField label="Email" value={form.email} onChange={set('email')} />
        <TextField label="Mobile" value={form.mobile} mono inputMode="numeric" onChange={(v) => set('mobile')(v.replace(/\D/g, '').slice(0, 10))} hint="Ten digits" />
        <TextField label="City" value={form.city} onChange={set('city')} />
        <TextField label="State" value={form.state} onChange={set('state')} />
      </div>

      {error && <Alert>{error}</Alert>}

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} loading={busy} disabled={form.fullName.trim().length < 2}>
          Save changes
        </Button>
      </div>
    </Dialog>
  );
}
